import { useState } from 'react';

export default function SolutionsCarousel() {
  const solutions = [
    {
      sport: 'NFL',
      category: 'Financial Management',
      feature: 'Salary Cap Dashboard',
      price: '2,499',
      image: 'https://public.readdy.ai/ai/img_res/21911421569c6fdc7ff8a06a76125a87.jpg',
      icon: 'ri-football-line',
      color: 'text-orange-500',
      steps: [
        { step: 1, action: 'Import all player contracts and salary data into centralized dashboard' },
        { step: 2, action: 'Set up real-time cap space alerts for contract negotiations' },
        { step: 3, action: 'Run restructure simulations for top 5 highest-paid players' },
        { step: 4, action: 'Identify $8-12M in immediate cap savings opportunities' },
        { step: 5, action: 'Generate compliance reports for league office submission' }
      ]
    },
    {
      sport: 'NBA',
      category: 'Health & Performance',
      feature: 'Player Load Management System',
      price: '2,199',
      image: 'https://readdy.ai/api/search-image?query=NBA%20player%20load%20management%20system%20interface%20displaying%20biometric%20data%2C%20fatigue%20levels%2C%20injury%20risk%20indicators%2C%20player%20health%20metrics%20and%20rest%20schedules%2C%20sports%20science%20software%2C%20modern%20health%20monitoring%20dashboard&width=300&height=300&seq=nba-solution&orientation=squarish',
      icon: 'ri-basketball-line',
      color: 'text-yellow-500',
      steps: [
        { step: 1, action: 'Connect wearable devices to track player biometrics in real-time' },
        { step: 2, action: 'Establish baseline fatigue thresholds for each player position' },
        { step: 3, action: 'Schedule automated rest days when fatigue exceeds 85% threshold' },
        { step: 4, action: 'Reduce injury risk by 40% through predictive analytics' },
        { step: 5, action: 'Optimize practice intensity based on game schedule density' }
      ]
    },
    {
      sport: 'Baseball',
      category: 'Analytics Platform',
      feature: 'Pitch Analytics & Scouting',
      price: '1,999',
      image: 'https://readdy.ai/api/search-image?query=baseball%20pitch%20analytics%20platform%20showing%20pitch%20tracking%20data%2C%20heat%20maps%2C%20velocity%20charts%2C%20spin%20rate%20analysis%2C%20and%20opponent%20scouting%20reports%2C%20sabermetrics%20software%20interface%2C%20detailed%20baseball%20statistics%20dashboard&width=300&height=300&seq=baseball-solution&orientation=squarish',
      icon: 'ri-ball-pen-line',
      color: 'text-lime-500',
      steps: [
        { step: 1, action: 'Upload last 30 games of pitch-by-pitch data for analysis' },
        { step: 2, action: 'Identify opponent weaknesses in specific pitch locations' },
        { step: 3, action: 'Create customized game plans for next 7 opponents' },
        { step: 4, action: 'Track pitcher fatigue patterns to prevent arm injuries' },
        { step: 5, action: 'Increase strikeout rate by 15% using data-driven pitch selection' }
      ]
    },
    {
      sport: 'Soccer',
      category: 'Tactical Tools',
      feature: 'Formation Optimization',
      price: '2,299',
      image: 'https://readdy.ai/api/search-image?query=soccer%20formation%20optimization%20tool%20interface%20showing%20tactical%20diagrams%2C%20player%20positioning%20heat%20maps%2C%20formation%20analysis%2C%20match%20strategy%20planning%2C%20football%20tactics%20software%2C%20interactive%20field%20visualization&width=300&height=300&seq=soccer-solution&orientation=squarish',
      icon: 'ri-football-fill',
      color: 'text-cyan-500',
      steps: [
        { step: 1, action: 'Analyze opponent formation patterns from last 10 matches' },
        { step: 2, action: 'Test 3 counter-formations using AI simulation engine' },
        { step: 3, action: 'Implement optimal formation in next training session' },
        { step: 4, action: 'Track possession improvement and defensive stability metrics' },
        { step: 5, action: 'Adjust player positioning to exploit opponent weak zones' }
      ]
    },
    {
      sport: 'Hockey',
      category: 'Team Strategy',
      feature: 'Line Combination Analyzer',
      price: '2,099',
      image: 'https://readdy.ai/api/search-image?query=hockey%20line%20combination%20analyzer%20interface%20displaying%20player%20chemistry%20metrics%2C%20ice%20time%20statistics%2C%20line%20performance%20data%2C%20shift%20analysis%2C%20professional%20hockey%20coaching%20software%2C%20detailed%20team%20strategy%20dashboard&width=300&height=300&seq=hockey-solution&orientation=squarish',
      icon: 'ri-hockey-puck-line',
      color: 'text-pink-500',
      steps: [
        { step: 1, action: 'Input current roster and analyze all possible line combinations' },
        { step: 2, action: 'Identify top 3 line combinations with highest chemistry scores' },
        { step: 3, action: 'Balance ice time distribution across all four lines' },
        { step: 4, action: 'Monitor shift-by-shift performance during next 5 games' },
        { step: 5, action: 'Increase goals scored by 22% through optimized line matching' }
      ]
    }
  ];

  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  return (
    <section id="solutions" className="bg-white py-32">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-start mb-16">
          <div>
            <h2 className="text-5xl font-bold text-gray-900 mb-3">
              ACTIONABLE <span className="text-red-500">IMPLEMENTATION PLANS</span>
            </h2>
            <p className="text-gray-600">Step-by-step solutions you can execute immediately</p>
          </div>
          <div className="bg-gray-800 text-white px-6 py-2 rounded-full text-sm font-medium whitespace-nowrap">
            5 Sports Covered
          </div>
        </div>

        <div className="overflow-x-auto pb-8 -mx-6 px-6">
          <div className="flex space-x-5 min-w-max">
            {solutions.map((solution, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl p-6 hover:shadow-xl transition-all duration-300 cursor-pointer group"
                style={{ width: '380px' }}
                onClick={() => setExpandedCard(expandedCard === index ? null : index)}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className={`w-16 h-16 flex items-center justify-center bg-white rounded-lg shadow-sm ${solution.color}`}>
                    <i className={`${solution.icon} text-3xl`}></i>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-gray-900">
                      ${solution.price}
                    </div>
                    <div className="text-xs text-gray-500">/month</div>
                  </div>
                </div>

                <div className="mb-6 rounded-lg overflow-hidden">
                  <img
                    src={solution.image}
                    alt={solution.feature}
                    className="w-full h-[240px] object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                <div className="space-y-2 mb-4">
                  <p className="text-sm text-gray-500">
                    {solution.sport} • {solution.category}
                  </p>
                  <h4 className="text-lg font-bold text-gray-900">
                    {solution.feature}
                  </h4>
                </div>

                <div className="bg-white rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h5 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                      Implementation Steps
                    </h5>
                    <i className={`ri-arrow-${expandedCard === index ? 'up' : 'down'}-s-line text-gray-400`}></i>
                  </div>
                  
                  <div className={`space-y-3 overflow-hidden transition-all duration-300 ${expandedCard === index ? 'max-h-96 opacity-100' : 'max-h-24 opacity-70'}`}>
                    {solution.steps.map((item, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <div className={`flex-shrink-0 w-7 h-7 rounded-full ${solution.color} bg-opacity-10 flex items-center justify-center`}>
                          <span className={`text-xs font-bold ${solution.color}`}>{item.step}</span>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed pt-0.5">
                          {item.action}
                        </p>
                      </div>
                    ))}
                  </div>

                  {expandedCard !== index && (
                    <p className="text-xs text-gray-400 italic pt-1">
                      Click to view all steps
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
